import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-amber-600 hover:text-amber-700 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a la página principal
          </Link>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 md:p-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Política de Privacidad de DogScan AI</h1>
          <p className="text-gray-600 mb-4">Última actualización: {new Date().toLocaleDateString()}</p>
          
          <div className="prose prose-amber max-w-none">
            <h2>Introducción</h2>
            <p>
              En DogScan AI ("nosotros", "nuestro", o "la plataforma"), respetamos su privacidad y nos comprometemos a proteger sus datos personales. 
              Esta política de privacidad le informará sobre cómo recopilamos, utilizamos, compartimos y protegemos sus datos personales cuando utiliza 
              nuestra aplicación y sitio web, así como sus derechos de privacidad.
            </p>
            
            <h2>Información que recopilamos</h2>
            <p>Podemos recopilar los siguientes tipos de información:</p>
            <ul>
              <li>
                <strong>Información personal</strong>: Nombre, dirección de correo electrónico, país, ciudad, teléfono, y cualquier información de 
                perfil proporcionada voluntariamente.
              </li>
              <li>
                <strong>Información sobre su mascota</strong>: Nombre, raza, edad, imágenes y datos de salud relacionados que usted proporcione.
              </li>
              <li>
                <strong>Información técnica</strong>: Dirección IP, tipo de dispositivo, tipo de navegador, configuración del sistema, ubicación general, y otros datos técnicos.
              </li>
              <li>
                <strong>Datos de autenticación</strong>: Si elige registrarse o iniciar sesión a través de un proveedor de terceros (como Facebook, Google, o Apple), 
                recibiremos ciertos datos de perfil de ese servicio.
              </li>
              <li>
                <strong>Datos de uso</strong>: Información sobre cómo utiliza nuestra plataforma, frecuencia, páginas visitadas, y funciones utilizadas.
              </li>
            </ul>
            
            <h2>Cómo recopilamos la información</h2>
            <p>Recopilamos información a través de las siguientes fuentes:</p>
            <ul>
              <li>
                <strong>Información que usted proporciona</strong>: Cuando se registra, crea un perfil, sube imágenes o utiliza nuestra plataforma.
              </li>
              <li>
                <strong>Proveedores de inicio de sesión</strong>: Cuando utiliza servicios de autenticación como Facebook, Google, o Apple.
              </li>
              <li>
                <strong>Uso automatizado</strong>: A través de cookies, píxeles, y tecnologías similares que recopilan información sobre cómo interactúa con nuestra plataforma.
              </li>
              <li>
                <strong>Análisis</strong>: Utilizamos herramientas analíticas para comprender cómo se utiliza nuestra plataforma.
              </li>
            </ul>
            
            <h2>Cómo utilizamos su información</h2>
            <p>Utilizamos la información recopilada para:</p>
            <ul>
              <li>Proporcionar nuestros servicios de análisis de salud para mascotas.</li>
              <li>Mejorar y personalizar su experiencia en nuestra plataforma.</li>
              <li>Comunicarnos con usted sobre nuestros servicios y actualizaciones.</li>
              <li>Procesar pagos y gestionar su cuenta.</li>
              <li>Analizar patrones de uso para mejorar nuestras funcionalidades.</li>
              <li>Proteger la seguridad e integridad de nuestra plataforma.</li>
              <li>Cumplir con obligaciones legales.</li>
            </ul>
            
            <h2>Compartir y divulgar su información</h2>
            <p>Podemos compartir su información en las siguientes circunstancias:</p>
            <ul>
              <li>
                <strong>Proveedores de servicios</strong>: Trabajamos con terceros que nos ayudan a operar nuestra plataforma 
                (procesamiento de pagos, hospedaje en la nube, análisis, etc.)
              </li>
              <li>
                <strong>Profesionales veterinarios</strong>: Si utiliza nuestros servicios de consulta, compartiremos la información 
                necesaria con los veterinarios para proporcionar estos servicios.
              </li>
              <li>
                <strong>Requisitos legales</strong>: Cuando sea necesario cumplir con la ley, procesos legales, o solicitudes gubernamentales.
              </li>
              <li>
                <strong>Protección de derechos</strong>: Para proteger los derechos, la propiedad o la seguridad de DogScan AI, nuestros usuarios o el público.
              </li>
              <li>
                <strong>Con su consentimiento</strong>: En otras circunstancias con su permiso explícito.
              </li>
            </ul>
            
            <h2>Servicios de inicio de sesión de terceros</h2>
            <p>
              Ofrecemos la posibilidad de registrarse e iniciar sesión utilizando servicios de terceros como Facebook, Google, o Apple. 
              Cuando elige esta opción, recopilamos información de su perfil que nos proporciona el servicio de terceros, de acuerdo 
              con su configuración de privacidad en ese servicio. Puede revisar y modificar qué información comparte con nosotros a 
              través de la configuración de privacidad de su cuenta en el servicio de terceros correspondiente.
            </p>
            
            <h3>Facebook Login</h3>
            <p>
              Si elige iniciar sesión a través de Facebook, recopilaremos la información que Facebook nos proporciona según la configuración 
              de su cuenta de Facebook y los permisos que otorgue. Esto puede incluir su nombre, dirección de correo electrónico, y foto de perfil. 
              No publicaremos en su muro de Facebook sin su permiso explícito.
            </p>
            
            <h2>Seguridad de los datos</h2>
            <p>
              Implementamos medidas de seguridad técnicas y organizativas para proteger sus datos personales contra el acceso no autorizado, 
              la divulgación, alteración, o destrucción. Sin embargo, ningún sistema es completamente seguro, por lo que no podemos garantizar 
              la seguridad absoluta de sus datos.
            </p>
            
            <h2>Retención de datos</h2>
            <p>
              Conservamos sus datos personales solo durante el tiempo necesario para cumplir con los fines para los que los recopilamos, 
              incluido el cumplimiento de requisitos legales, contables o de informes.
            </p>
            
            <h2>Sus derechos de privacidad</h2>
            <p>Dependiendo de su ubicación, puede tener los siguientes derechos:</p>
            <ul>
              <li>Acceder a sus datos personales.</li>
              <li>Corregir datos inexactos.</li>
              <li>Eliminar sus datos en determinadas circunstancias.</li>
              <li>Restringir u oponerse al procesamiento de sus datos.</li>
              <li>Solicitar la portabilidad de sus datos.</li>
              <li>Retirar el consentimiento para el procesamiento de datos.</li>
            </ul>
            <p>
              Para ejercer estos derechos, póngase en contacto con nosotros a través de la información proporcionada en la sección "Contáctenos".
            </p>
            
            <h2>Niños</h2>
            <p>
              Nuestros servicios no están dirigidos a menores de 16 años. No recopilamos intencionadamente información personal de niños. 
              Si es padre o tutor y cree que su hijo nos ha proporcionado información, por favor contáctenos.
            </p>
            
            <h2>Cambios a esta política</h2>
            <p>
              Podemos actualizar esta política de privacidad periódicamente. Le notificaremos cualquier cambio importante publicando la nueva 
              política de privacidad en esta página y, si los cambios son significativos, le proporcionaremos un aviso más prominente.
            </p>
            
            <h2>Contáctenos</h2>
            <p>
              Si tiene preguntas sobre esta política de privacidad o nuestras prácticas, por favor contáctenos en:
            </p>
            <p className="font-medium">
              Correo electrónico: contacto@dogscanai.com<br />
              Dirección: P.º del Río 28Paseos de Taxqueña, Coyoacán, 04250 Ciudad de México, CDMX
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 